<template>
  <div class="min-h-screen bg-gray-50">
    <Head title="Reportes" />
    
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div class="flex items-center justify-between mb-8">
        <div class="flex items-center space-x-4">
          <h1 class="text-2xl font-bold text-gray-900">Reportes</h1>
          <span class="px-3 py-1 text-sm font-medium text-indigo-700 bg-indigo-100 rounded-full">
            Generador de reportes
          </span>
        </div>
      </div>

      <!-- Report Form Section -->
      <div class="bg-white shadow-sm rounded-lg overflow-hidden">
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- Date Range -->
            <div class="space-y-6">
              <h3 class="text-lg font-medium text-gray-900">Rango de fechas</h3>
              <div class="space-y-4">
                <text-input 
                  v-model="form.from" 
                  label="Desde" 
                  type="date"
                  class="w-full"
                />
                <text-input 
                  v-model="form.to" 
                  label="Hasta" 
                  type="date"
                  class="w-full"
                />
              </div>
            </div>

            <!-- Report Options -->
            <div class="space-y-6">
              <h3 class="text-lg font-medium text-gray-900">Opciones del reporte</h3>
              <div class="space-y-4">
                <select-input 
                  v-model="form.report_target" 
                  label="Objeto del reporte"
                  class="w-full"
                >
                  <option value="orders">Cuotas (Ventas)</option>
                  <option value="payments">Pagos Recibidos</option>
                </select-input>

                <select-input 
                  v-if="form.report_target === 'orders'"
                  v-model="form.modality" 
                  label="Tipo de reporte"
                  class="w-full"
                >
                  <option value="">Seleccionar tipo</option>
                  <option value="advance">Detallado</option>
                  <option value="simple">Simple</option>
                </select-input>

                <select-input 
                  v-if="form.report_target === 'orders'"
                  v-model="form.order_state" 
                  label="Estado de las órdenes"
                  class="w-full"
                >
                  <option value="all">Todos los estados</option>
                  <option value="request">Pendiente por pago</option>
                  <option value="payRevition">Pago en revisión</option>
                  <option value="finish">Cuota pagada</option>
                </select-input>
              </div>
            </div>

            <!-- Additional Options -->
            <div class="space-y-6" v-if="form.report_target === 'orders'">
              <h3 class="text-lg font-medium text-gray-900">Filtros adicionales</h3>
              <div class="space-y-4">
                <select-input 
                  v-model="form.nulled" 
                  label="Incluir anulados"
                  class="w-full"
                >
                  <option value="0">No incluir anulados</option>
                  <option value="1">Incluir anulados</option>
                </select-input>

                <select-input 
                  v-model="form.type" 
                  label="Formato de exportación"
                  class="w-full"
                >
                  <option value="excel">Excel (.xlsx)</option>
                  <option value="pdf">PDF (.pdf)</option>
                </select-input>
              </div>
            </div>
          </div>

          <!-- Generate Button -->
          <div class="mt-8 flex justify-end">
            <button 
              @click="generateReport"
              class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              :disabled="isGenerating"
            >
              <svg 
                v-if="isGenerating" 
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" 
                fill="none" 
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg 
                v-else 
                class="h-5 w-5 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {{ isGenerating ? 'Generando reporte...' : 'Generar reporte' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput.vue'
import SelectInput from "@/Shared/SelectInput.vue"

export default {
  components: {
    SelectInput,
    TextInput,
    Head,
  },
  layout: Layout,
  props: {
    order: String,
  },
  data() {
    return {
      isGenerating: false,
      form: this.$inertia.form({
        from: '',
        to: '',
        type: 'excel',
        full: false,
        nulled: 0,
        modality: 'simple',
        orderNumber: Math.floor(Math.random() * (59999 - 10) + 10),
        order_state: 'all',
        report_target: 'orders'
      })
    }
  },
  methods: {
    generateReport() {
      if (!this.form.from || !this.form.to) {
        alert('Por favor seleccione un rango de fechas')
        return
      }

      this.isGenerating = true
      
      const endpoint = this.form.report_target === 'payments' ? '/reports/payments' : '/orders/report';
      
      this.form.post(endpoint, {
        onSuccess: () => {
          this.isGenerating = false
        },
        onError: () => {
          this.isGenerating = false
        }
      })
    }
  }
}
</script>

<style scoped>
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 200ms;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
